import React, { useState } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import ScrollToTopButton from "../topbutton/Top";
import { StyleProvider } from "../../contexts/StyleContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Helmet } from "react-helmet-async";
import "./Privacy.scss";

const KEY_NAME = "anl_bid";
const EMAIL = "privacy@anilgulati.com";

const readKey = () => {
  try {
    return window.localStorage.getItem(KEY_NAME) || "";
  } catch {
    return "";
  }
};

export default function Privacy() {
  const darkPref = window.matchMedia("(prefers-color-scheme: dark)");
  const [isDark, setIsDark] = useLocalStorage("isDark", darkPref.matches);
  const changeTheme = () => setIsDark(!isDark);

  const [key, setKey] = useState(readKey());
  const [note, setNote] = useState("");

  const copyKey = async () => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setNote("Copied.");
    } catch {
      setNote("Could not copy. Select the key above and copy it by hand.");
    }
  };

  const resetKey = () => {
    if (window.tracker && typeof window.tracker.resetIdentity === "function") {
      window.tracker.resetIdentity();
    } else {
      try {
        window.localStorage.removeItem(KEY_NAME);
        window.sessionStorage.removeItem("anl_session");
        window.sessionStorage.removeItem("anl_identity");
      } catch {}
    }
    setKey("");
    setNote("Key cleared. A new one is created on your next visit.");
  };

  return (
    <div className={isDark ? "dark-mode" : null}>
      <Helmet>
        <title>Privacy Policy | Anil Gulati</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://anilgulati.com/privacy" />
      </Helmet>
      <StyleProvider value={{ isDark, changeTheme }}>
        <Header />

        <main className="privacy-page">
          <section className="privacy-hero">
            <h1>Privacy Policy</h1>
            <p className="privacy-meta">Anil Gulati · Last updated 2 October 2026</p>
          </section>

          <section className="privacy-section">
            <h2>The short version</h2>
            <p>
              This site uses a small analytics script I built myself. No
              cookies, no ad trackers, no third-party analytics, and I don't
              sell or share your data. I just want to know which pages get read
              and keep bots out.
            </p>
            <p>
              Questions, or want your data deleted? Email{" "}
              <a className="privacy-link" href={`mailto:${EMAIL}`}>
                {EMAIL}
              </a>
              .
            </p>
          </section>

          <section className="privacy-section">
            <h2>Your browser key</h2>
            <p>
              The site saves one random code in your browser's local storage,
              called <code>{KEY_NAME}</code>. It helps me count visitors and tell
              real people from bots. It is not your name or contact info, and I
              can't tell who you are from it. It is a pseudonymous ID for this
              browser only, and I still treat it as personal data.
            </p>

            <div className="privacy-card">
              <div className="privacy-key-label">Your key in this browser</div>
              <div className="privacy-key">
                {key ? key : "No key stored in this browser right now."}
              </div>
              <div className="privacy-actions">
                <button
                  type="button"
                  className="privacy-btn"
                  onClick={copyKey}
                  disabled={!key}
                >
                  Copy key
                </button>
                <button
                  type="button"
                  className="privacy-btn privacy-btn-alt"
                  onClick={resetKey}
                  disabled={!key}
                >
                  Reset key
                </button>
              </div>
              {note ? <p className="privacy-note">{note}</p> : null}
            </div>

            <p>
              <strong>Want your data deleted?</strong> Copy your key with the
              button above and email it to me. I'll delete everything linked to
              it. The key lives in one browser on one device, so other devices
              have their own keys. Without a key I can't match records to you,
              but everything deletes itself after 90 days anyway.
            </p>
            <p>
              <strong>Want a fresh start?</strong> Press Reset key. Your next
              visit gets a new random key. Records tied to the old key stay
              until they expire, unless you email me the old key first.
            </p>
          </section>

          <section className="privacy-section">
            <h2>What I see when you visit</h2>
            <p>
              Basic visit info: which page you opened, where you came from, how
              long you stayed, how far you scrolled, and which links you
              clicked. I also get technical details about your browser and
              device (things like browser type, screen size, language,
              timezone and connection type), plus a few device characteristics
              I use to spot bots and automated traffic.
            </p>
            <p>
              I get your IP address too. I use it to block abusive traffic. More on
              that next.
            </p>
          </section>

          <section className="privacy-section">
            <h2>IP addresses</h2>
            <ul>
              <li>
                For blocking abusive traffic, a scrambled version of your IP is
                kept for up to 24 hours.
              </li>
              <li>
                In analytics records, I keep only that scrambled version, never
                the plain IP, for up to 90 days. It is hashed with a secret key,
                so I can't turn it back into your IP.
              </li>
            </ul>
          </section>


          <section className="privacy-section">
            <h2>How long I keep things</h2>
            <p>
              Analytics records, including the browser key, are deleted
              automatically after 90 days.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Where it's stored</h2>
            <p>
              On Amazon Web Services in Mumbai (ap-south-1). Transfers from
              Europe are covered by the standard contractual clauses in AWS's
              data processing terms.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Why I'm allowed to do this</h2>
            <p>
              Under GDPR the basis is legitimate interest: keeping the site
              secure and understanding how it's used. I apply the same rules to
              everyone, wherever you are.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Your rights</h2>
            <p>
              You can ask to see, delete or object to the use of your data. Just
              email me. If you're not happy with my answer, you can complain to
              your local data protection authority. In France that's the CNIL
              (cnil.fr), and in other countries it's your national regulator.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes</h2>
            <p>
              If this changes, I'll update this page and the date at the top.
            </p>
          </section>
        </main>

        <Footer />
        <ScrollToTopButton />
      </StyleProvider>
    </div>
  );
}